import { userStorage } from "$root/functional-areas/user/userStorage";
import { type AgGridColState, zAgGridColStateV1 } from "$root/ui-lib/table/col-state-schema";
import { tryJsonParse } from "$root/utils";
import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";

/** A single column entry from a table's persisted AG Grid column state. */
export type PersistedColumn = AgGridColState["data"][number];

/**
 * Reads and parses the AG Grid column state persisted under `colStateKey`.
 * Returns `null` when the user has no stored preference (or it fails to parse / validate).
 */
export function readPersistedColumns(colStateKey: string): PersistedColumn[] | null {
	const stored = userStorage.get(colStateKey);
	if (!stored) {
		return null;
	}
	const parsed = tryJsonParse(stored, null, (x) => zAgGridColStateV1().parse(x));
	if (!parsed) {
		return null;
	}
	return parsed.data;
}

/**
 * Pin-section rank used to order exported columns the way the table renders them: pinned-left columns
 * first, unpinned in the middle, pinned-right last. AG Grid persists `pinned` as `"left"`/`"right"`,
 * with the legacy `true` meaning pinned-left.
 */
function pinnedRank(pinned: PersistedColumn["pinned"]): number {
	if (pinned === "left" || pinned === true) {
		return 0;
	}
	if (pinned === "right") {
		return 2;
	}
	return 1;
}

/**
 * Col ids of the visible columns, ordered the way the table renders them: pinned-left first,
 * pinned-right last, unpinned in between, each section keeping its relative order
 * (`Array.prototype.sort` is stable).
 */
export function orderedVisibleColIds(columns: PersistedColumn[]): string[] {
	return columns
		.filter((column) => !column.hide)
		.sort((a, b) => pinnedRank(a.pinned) - pinnedRank(b.pinned))
		.map((column) => column.colId);
}

/**
 * Derives the export sort criteria from a persisted column state: every column with an active `sort`,
 * ordered by `sortIndex`, mapped to the UUID of its projected concept. Columns whose `colId` has no
 * matching concept (e.g. a hidden column that isn't projected in the export) are skipped, since the
 * export can only sort by concepts it actually projects.
 */
export function buildSortCriteria(
	columns: PersistedColumn[],
	conceptByColId: ReadonlyMap<string, smartWorkspaces.ParameterizedConceptDTO>,
): smartWorkspaces.SortCriterionDTO[] {
	return columns
		.filter((column) => column.sort != null)
		.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
		.flatMap((column) => {
			const uuid = conceptByColId.get(column.colId)?.uuid;
			if (!uuid) {
				return [];
			}
			return [{ parameterizedConceptUuid: uuid, direction: column.sort === "asc" ? "ASC" : "DESC" }];
		});
}
